import React, { useContext, useEffect } from 'react'
import { Container } from '@awazevr/design-system.components.container'
import styles from './Hero.module.scss'
import {
  HeroImage,
  HeroImageProps,
} from '@awazevr/design-system.components.hero-image'
import { Text } from '@awazevr/design-system.components.text'

import {
  BaseGrid,
  BaseGridItem,
} from '@awazevr/design-system.components.base-grid'

import { SearchFormConfig } from '@awazevr/design-system.compositions.search-form'
import { getUserDeviceType, setImageParams } from 'src/utils/helpers/helpers'

import { IHeaderOptionsModuleFields } from 'contentful/types/contentful-modules'
import { BodyTypographyScale } from '@awazevr/design-system.utils.react'
import { GlobalProps } from '../../utils/globalProps'
import { useTrustpilotUspsBannerVisible } from 'src/lib/optimizely/experiments/use-trustpilot-usps-banner-visible'
import { useIntl } from 'react-intl'
import { TrustpilotConfiguration } from '../TrustpilotWidget/trustpilot-widget-config'
import { PlacesSearchConfigType } from '@/components/SearchForm/SearchForm.types'
import { SearchFormComponent } from '@/components/SearchForm/SearchForm'
import { AlertBanner } from '@awazevr/design-system.components.alert-banner'
import { IAlertBanner } from '../../../@types/generated/contentful-global'
import { TrustpilotUspsBanner } from '@awazevr/design-system.compositions.trustpilot-usps-banner'
import { LinkModule } from '@/components/links/link.module'
import { withCtfassetsUrlFix } from '@/components/HOC/withCtfassetsUrlFix'
import { useHeroTitleVisible } from '../../lib/optimizely/experiments/use-hero-title-visible'
import { HeroImageExperimentContainer } from '../HeroExperiment/HeroExperimentContainer'

type ExtendedHeroImageProps = HeroImageProps & {
  style: {
    minHeight: string
  }
}

export const ExtendedHeroImage = HeroImage as React.FC<ExtendedHeroImageProps>

export const Hero: React.FC<HeroProps> = ({
  headerOptions,
  searchFormConfig,
  placesConfig,
  alertBanner,
  experimentEnabled,
}) => {
  const [heroTitle, setHeroTitle] = React.useState(
    headerOptions?.headlineTitle ?? '',
  )
  const heroImages = headerOptions?.heroImages?.map((image) =>
    setImageParams(image.fields.file?.url, {
      // if this is changed to a different value,
      // the corresponding change should be made in the hero-image preload component
      // src/components/ResponsiveImage/LcpImagePreload.tsx
      q: '80',
      fm: 'webp',
    }),
  )

  const isDesktop = getUserDeviceType() === 'desktop'

  const { contentfulLocale, brand, awazeClient } = useContext(GlobalProps)

  const showAlertBanner = Boolean(alertBanner?.fields?.text?.trim().length)

  const { showUsps: OptimizelyShowUsps, uspsVariant } =
    useTrustpilotUspsBannerVisible()

  const showUsps =
    headerOptions.showUsps !== undefined && headerOptions.showUsps !== null
      ? headerOptions?.showUsps
      : OptimizelyShowUsps

  const showTrustpilotWidget = headerOptions.showTrustpilotBanner || false

  const translationKeyPrefixByBrand = uspsVariant
    ? `${brand}.${uspsVariant}`
    : `${brand}`

  const { titleVariant, isLoaded } = useHeroTitleVisible()

  const heroTranslationKeyPrefixByBrand = titleVariant
    ? `${brand}.${titleVariant}`
    : `${brand}`

  const { formatMessage } = useIntl()
  const { usp1, usp2, usp3 } = headerOptions || {}
  const uspMessagesList = [usp1, usp2, usp3].filter(
    (usp: string | null | undefined) =>
      usp !== null && usp !== undefined && usp.trim().length > 0,
  )
  // Use brand-specific translation keys for Trustpilot USPS banner, fallback to default English
  const trustpilotUsps =
    uspMessagesList?.length === 3 // number of usps messages alloweed is 3
      ? uspMessagesList
      : [
          formatMessage({
            id: `trustpilot.usps.1.${translationKeyPrefixByBrand}`,
            defaultMessage: formatMessage({
              id: 'trustpilot.usps.1',
              defaultMessage: 'Book for 2026 - low first payment',
            }),
          }),
          formatMessage({
            id: `trustpilot.usps.2.${translationKeyPrefixByBrand}`,
            defaultMessage: formatMessage({
              id: 'trustpilot.usps.2',
              defaultMessage: 'Book 2026 for 2025 prices',
            }),
          }),
          formatMessage({
            id: `trustpilot.usps.3.${translationKeyPrefixByBrand}`,
            defaultMessage: formatMessage({
              id: 'trustpilot.usps.3',
              defaultMessage: 'Book direct for the best price',
            }),
          }),
        ]

  useEffect(() => {
    if (isLoaded) {
      setHeroTitle(
        formatMessage({
          id: `hero-title.${heroTranslationKeyPrefixByBrand}`,
          defaultMessage: headerOptions?.headlineTitle,
        }),
      )
    }
  }, [
    isLoaded,
    heroTranslationKeyPrefixByBrand,
    formatMessage,
    headerOptions?.headlineTitle,
  ])

  const heroContrainer = (
    <Container>
      <BaseGrid align="center" flush>
        <BaseGridItem columns={12} data-ga="hero-combined-content">
          <>
            <Text
              as={headerOptions.headlineOptions?.fields?.as || 'h1'}
              data-ga="hero-header-title"
              className={styles.heading}
              sizes={[
                (headerOptions.headlineOptions?.fields?.mobileSize ||
                  headerOptions.headlineOptions?.fields?.size ||
                  200) as BodyTypographyScale,
                (headerOptions.headlineOptions?.fields?.size ||
                  200) as BodyTypographyScale,
                (headerOptions.headlineOptions?.fields?.size ||
                  200) as BodyTypographyScale,
              ]}
            >
              {awazeClient === 'guest-app'
                ? headerOptions.headlineTitle
                : heroTitle
                ? heroTitle
                : ''}
            </Text>
            <Text
              as={'p'}
              data-ga="hero-header-text"
              className={styles.subtitle}
            >
              {headerOptions?.headlineSubtitle}
            </Text>
            <SearchFormComponent
              config={searchFormConfig}
              placesConfig={placesConfig}
            />
          </>
        </BaseGridItem>
      </BaseGrid>
    </Container>
  )

  return (
    <>
      {showAlertBanner && awazeClient !== 'guest-app' && (
        <AlertBanner
          variant="caution"
          renderContainer
          data-testid="alert-banner"
        >
          {alertBanner?.fields?.text}

          {alertBanner?.fields?.link && (
            <div>
              <LinkModule
                variant="text"
                sizes={200}
                underline={false}
                openInNewTab={alertBanner?.fields?.link?.fields?.openInNew}
                colourScheme="brand-link"
                href={alertBanner?.fields?.link?.fields?.href}
              >
                {alertBanner?.fields?.link?.fields?.displayText}
              </LinkModule>
            </div>
          )}
        </AlertBanner>
      )}
      {!showAlertBanner && awazeClient !== 'guest-app' && (
        <div
          className={brand === 'jamesVillas' ? styles.uspsBannersJV : undefined}
        >
          <TrustpilotUspsBanner
            key={`${headerOptions?.showTrustpilotBanner}-${showUsps}`} // Force remount on prop change, to make sure nav buttons work in carousel
            brand={brand}
            locale={contentfulLocale}
            trustPilotConfig={TrustpilotConfiguration}
            showTrustpilotWidget={showTrustpilotWidget}
            showUsps={showUsps}
            usps={trustpilotUsps}
          />
        </div>
      )}

      {experimentEnabled ? (
        <HeroImageExperimentContainer
          isDesktop={isDesktop}
          heroContrainer={heroContrainer}
          heroImages={heroImages}
        />
      ) : (
        <ExtendedHeroImage
          backgroundImages={heroImages}
          data-ga="hero-image"
          style={{
            minHeight: '0', // needed to override the default min-height
            // to make the hero image adapt to the height of the content
          }}
        >
          <div className={styles.hero}>{heroContrainer}</div>
        </ExtendedHeroImage>
      )}
    </>
  )
}

export const HeroWithImageCompression = withCtfassetsUrlFix(Hero)

type HeroProps = {
  headerOptions?: IHeaderOptionsModuleFields
  searchFormConfig?: SearchFormConfig
  placesConfig?: PlacesSearchConfigType
  alertBanner?: IAlertBanner
  experimentEnabled?: boolean
}
